import { dayText } from "./accounts";
import type { SalesRow, ScorecardWeek } from "./api";
import { emailName } from "./calls";
import { LOST_REASONS, LOST_TEXT } from "./leads";

export const WEEK_CHOICES = [8, 13, 26];

export interface Metric {
  key: "calls_at_fault" | "overflows_per_100" | "accuracy" | "done_share";
  label: string;
  note: string;
  /** The note when an older API gives only the ratio. */
  oldNote?: string;
}

export const ACCURACY_HELP = "100% less the gallon error of each done stop, as a share of the estimated gallons. Errors up and down do not cancel. Higher is better.";

export const METRICS: Metric[] = [
  { key: "calls_at_fault", label: "Calls at fault", note: "Calls about a problem that the collector caused. Lower is better." },
  {
    key: "overflows_per_100",
    label: "Overflows for each 100 accounts",
    note: "Overflow calls in one cycle of 4 weeks up to the end of the week. Lower is better.",
  },
  {
    key: "accuracy",
    label: "Estimate accuracy",
    note: ACCURACY_HELP,
    oldNote: "Actual gallons ÷ estimated gallons of the done stops. It is usually 0.8 to 1.2.",
  },
  { key: "done_share", label: "Planned stops done", note: "The planned stops of the cycle board that got a pickup." },
];

/** True when the API gives the accuracy from 0 to 100%; an older API gives only the ratio. */
export const hasAccuracyPct = (week: ScorecardWeek) => week.accuracy_pct !== undefined;

/** The accuracy of a week: the percent, else the old ratio. */
export const accuracyValue = (week: ScorecardWeek): number | null => (hasAccuracyPct(week) ? (week.accuracy_pct ?? null) : week.accuracy);

/** The number of one metric in one week, or null when the week has no base for it. */
export function metricValue(key: Metric["key"], week: ScorecardWeek): number | null {
  if (key === "done_share") return week.planned_stops ? Math.round((100 * week.done_stops) / week.planned_stops) : null;
  if (key === "accuracy") return accuracyValue(week);
  return week[key];
}

/** The note of a metric card: the old note when the API gives no accuracy percent. */
export const metricNote = (metric: Metric, weeks: ScorecardWeek[]) =>
  metric.oldNote && weeks.length > 0 && !weeks.some(hasAccuracyPct) ? metric.oldNote : metric.note;

/** "Estimates 6% high overall" from the bias; nothing when the API gives no bias. */
export function biasText(week: ScorecardWeek): string {
  if (week.bias == null) return "";
  const pct = Math.round(Math.abs(week.bias) * 100);
  if (pct === 0) return "Estimates were right overall";
  return `Estimates ${pct}% ${week.bias < 0 ? "high" : "low"} overall`;
}

/** "1,154 gal picked up; 978 gal estimated" of the done stops of a week, in whole gallons as the tile hint. */
export const gallonsText = (week: ScorecardWeek, gal: (n: number) => string) =>
  `${gal(Math.round(week.actual_gal))} picked up; ${gal(Math.round(week.estimated_gal))} estimated`;

/** The hover text of the accuracy of a week: the bias, then the gallons. */
export function accuracyHelp(week: ScorecardWeek, gal: (n: number) => string): string {
  if (accuracyValue(week) === null) return "No done stop with an estimate in this week.";
  const gallons = `${gallonsText(week, gal)}.`;
  const bias = biasText(week);
  return bias ? `${bias}. ${gallons}` : gallons;
}

export function metricText(key: Metric["key"], week: ScorecardWeek): string {
  const value = metricValue(key, week);
  if (value === null) return "No value";
  if (key === "accuracy") return hasAccuracyPct(week) ? `${Math.round(value)}%` : value.toFixed(2);
  if (key === "overflows_per_100") return value.toFixed(1);
  if (key === "done_share") return `${value}% (${week.done_stops} of ${week.planned_stops})`;
  return String(value);
}

/** The weeks to show from the URL parameter "weeks", else the first choice. */
export function readWeeks(search: string): number {
  const found = Number(new URLSearchParams(search).get("weeks"));
  return WEEK_CHOICES.includes(found) ? found : WEEK_CHOICES[0];
}

/** Return the search string with the weeks set; the first choice is the default and is not written. */
export function writeWeeks(search: string, weeks: number): string {
  const params = new URLSearchParams(search);
  if (weeks === WEEK_CHOICES[0]) params.delete("weeks");
  else params.set("weeks", String(weeks));
  const out = params.toString();
  return out ? `?${out}` : "";
}

/** The complete weeks only, so that the line of a card ends on the week of its number. */
export function completeWeeks(weeks: ScorecardWeek[]): ScorecardWeek[] {
  return weeks.filter((week) => !week.partial);
}

/** The newest week that is complete, for the headline of each metric. */
export function latestComplete(weeks: ScorecardWeek[]): ScorecardWeek | undefined {
  return [...weeks].reverse().find((week) => !week.partial);
}

export function weekLabel(week: ScorecardWeek): string {
  return `${dayText(week.week_start)} to ${dayText(week.week_end)}`;
}

export interface Spark {
  path: string;
  last: { x: number; y: number } | null;
  max: number;
}

/** A line through the values from the left to the right, from zero up. A null value breaks the line. */
export function spark(values: (number | null)[], width: number, height: number, pad = 3): Spark {
  const known = values.filter((value): value is number => value !== null);
  const max = Math.max(0, ...known);
  const step = values.length > 1 ? (width - 2 * pad) / (values.length - 1) : 0;
  const yOf = (value: number) => {
    const span = height - 2 * pad;
    return Math.round((height - pad - (max > 0 ? (value / max) * span : 0)) * 10) / 10;
  };
  let path = "";
  let gap = true;
  let last: Spark["last"] = null;
  values.forEach((value, index) => {
    if (value === null) {
      gap = true;
      return;
    }
    const x = Math.round((pad + index * step) * 10) / 10;
    const y = yOf(value);
    path += `${gap ? "M" : "L"}${x} ${y} `;
    gap = false;
    last = { x, y };
  });
  return { path: path.trim(), last, max };
}

/** A share of the accounts of a row, or a dash when the row has no accounts. */
export const shareText = (value: number | null): string => (value === null ? "–" : `${Math.round(value)}%`);

export function hoursText(row: SalesRow): string {
  if (row.hours_per_win === null || !row.wins) return "–";
  const minutes = row.minutes / row.wins;
  return minutes < 60 ? `${Math.round(minutes)} min` : `${row.hours_per_win} h`;
}

/** The salesperson of a row: the short email name, or House for the accounts with no salesperson. */
export function personText(row: SalesRow): string {
  if (!row.key) return "House (no salesperson)";
  return row.name ? emailName(row.name) : "Former member";
}

export const stateText = (row: SalesRow): string => row.key || "No state";

/** The counts with the largest first, with the text of each key. */
export function countRows(counts: Record<string, number>, label: (key: string) => string): [string, number][] {
  return Object.entries(counts)
    .map(([key, n]): [string, number] => [label(key), n])
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
}

export const lostText = (key: string): string =>
  (LOST_REASONS as readonly string[]).includes(key) ? LOST_TEXT[key as (typeof LOST_REASONS)[number]] : key;

export const collectorText = (key: string): string => key || "None named";
